import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuth } from '../../slices/authSlice';
import { restoreStoreContext } from '../../utils/storeContext';

/**
 * SessionChecker - Verifies backend session on app load
 * 
 * Problem: Frontend stores user in localStorage (persists forever)
 *          Backend session expires after 24 hours
 *          Result: User appears logged in but all API calls fail
 * 
 * Solution: On app load, verify backend session is still valid
 *           If invalid, clear localStorage and force re-login
 * 
 * Also restores store context (x-storeid header) from localStorage
 * so that Sales Assistants and other employees can access their store data
 */
const SessionChecker = ({ children }) => {
  const dispatch = useDispatch();
  const { user, sessionChecked } = useSelector((state) => state.auth);

  useEffect(() => {
    // CRITICAL: Restore store context FIRST before any API calls
    // This must happen before checkAuth() to ensure the x-storeid header is set
    const restoredStore = restoreStoreContext();
    
    // Only check if we have a user in localStorage but haven't verified the session yet
    if (user && !sessionChecked) {
      console.log('Verifying backend session for user:', user.email || user.employeeId);
      
      if (restoredStore) {
        console.log('Store context restored before session check:', restoredStore._id);
      } else {
        console.warn('No store context found in localStorage');
      }
      
      // Verify session with backend (now with correct x-storeid header)
      dispatch(checkAuth());
    } else if (!user) {
      // No user in localStorage, no need to check
      // This is handled by the regular flow in authSlice
    }
  }, [dispatch, user, sessionChecked]);

  // Always render children - the RequireAuth component handles redirects
  return children;
};

export default SessionChecker;

